import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  limit,
  Timestamp,
  writeBatch,
} from 'firebase/firestore'
import { db, auth } from './firebase'

export interface FrequencyPattern {
  mode: 'hourly' | 'daily' | 'weekly' | 'monthly' | 'custom'
  times?: string[] // "HH:MM" format
  daysOfWeek?: number[] // 0(Sun)-6(Sat)
  daysOfMonth?: number[] // 1-31 for monthly mode
  startHour?: number // for hourly mode
  endHour?: number // for hourly mode
  timezone?: string // IANA timezone
}

// Admin UI Challenge interface (compatible with existing admin components)
export interface Challenge {
  id: string
  name: string
  description?: string
  category: string
  icon?: string
  status: 'active' | 'inactive' | 'draft' | 'archived'
  language?: string
  defaultFrequency?: {
    mode: 'hourly' | 'daily' | 'weekly' | 'monthly' | 'custom'
    times?: string[]
    timezone?: string
  }
  order?: number
  totalUsers?: number
  createdAt: string | Date
  updatedAt?: string | Date
}

// Admin UI Challenge Translation interface
export interface ChallengeTranslation {
  id: string
  challengeId: string
  language: string
  name: string
  description: string
  slug?: string
  createdAt?: string | Date
  updatedAt?: string | Date
}

// Admin UI Category interface
export interface Category {
  id: string
  name: string
  description?: string
  icon: string
  color?: string
  status: 'active' | 'inactive' | 'draft' | 'archived'
  language?: string
  order?: number
  slug?: string
  createdAt: string | Date
  updatedAt?: string | Date
}

// Admin UI Category Translation interface
export interface CategoryTranslation {
  id: string
  categoryId: string
  language: string
  name: string
  description: string
  slug?: string
  createdAt?: string | Date
  updatedAt?: string | Date
}




export interface User {
  id: string
  displayName: string
  email: string
  role: 'user' | 'admin' | 'moderator'
  status: 'active' | 'suspended' | 'deleted'
  locale: string
  photoURL?: string
  settings: {
    notifications: boolean
    analytics: boolean
  }
  createdAt: Timestamp | string
  updatedAt: Timestamp | string
  lastLoginAt?: Timestamp | string
}

export interface AdminStats {
  challengesCount: number
  categoriesCount: number
  usersCount: number
  languagesCount: number
}

export interface Language {
  id: string
  code: string
  name: string
  nativeName: string
  flag: string
  isActive: boolean
  order: number
  createdAt: Timestamp | string
  updatedAt: Timestamp | string
}

class AdminService {
  // Challenge Management - Updated to use optimized schema
  async getChallenges(language: string = 'en'): Promise<Challenge[]> {
    console.log(`🔍 Fetching challenges for language: ${language}`)
    
    try {
      // Get base challenges and translations in parallel
      const [challengesSnapshot, translationsSnapshot] = await Promise.all([
        getDocs(query(collection(db, 'challenges'), orderBy('order', 'asc'))),
        getDocs(query(collection(db, 'challengeTranslations'), where('language', '==', language)))
      ])
      
      console.log(`📊 Found ${challengesSnapshot.size} base challenges and ${translationsSnapshot.size} translations for ${language}`)
      
      // Create a map of translations by challenge ID
      const translationsMap = new Map<string, any>()
      translationsSnapshot.docs.forEach(doc => {
        const translation = doc.data()
        translationsMap.set(translation.challengeId, translation)
      })
      
      // Combine base challenges with their translations
      const challenges = challengesSnapshot.docs.map(doc => {
        const baseChallenge = doc.data()
        const translation = translationsMap.get(doc.id)
        
        // If no translation exists for this language, skip this challenge
        if (!translation) {
          console.log(`⚠️ No translation found for challenge ${doc.id} in language ${language}`)
          return null
        }
        
        // Combine base challenge data with translation data
        return {
          id: doc.id,
          name: translation.name,
          description: translation.description,
          category: baseChallenge.categoryId || 'general',
          icon: baseChallenge.icon || '🎯',
          status: baseChallenge.status || 'active',
          language: translation.language,
          defaultFrequency: baseChallenge.defaultFrequency || {
            mode: 'daily',
            times: ['09:00'],
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
          order: baseChallenge.order || 0,
          totalUsers: baseChallenge.metadata?.totalUsers || 0,
          createdAt: baseChallenge.metadata?.createdAt || new Date(),
          updatedAt: baseChallenge.metadata?.updatedAt || new Date(),
        } as Challenge
      }).filter(Boolean) as Challenge[]
      
      console.log(`✅ Found ${challenges.length} challenges with translations for ${language}`)
      console.log('🎯 Challenge IDs:', challenges.map(c => c.id))
      return challenges
      
    } catch (error) {
      console.error('❌ Error fetching challenges:', error)
      return []
    }
  }

  async getChallenge(id: string): Promise<Challenge | null> {
    try {
      console.log(`🔍 Looking for challenge with ID: "${id}"`)
      
      // Get base challenge and English translation in parallel
      const [challengeDoc, translationSnapshot] = await Promise.all([
        getDoc(doc(db, 'challenges', id)),
        getDocs(query(
          collection(db, 'challengeTranslations'), 
          where('challengeId', '==', id),
          where('language', '==', 'en')
        ))
      ])
      
      if (!challengeDoc.exists()) {
        console.warn(`❌ Challenge with ID "${id}" does not exist in the database`)
        return null
      }
      
      const baseChallenge = challengeDoc.data()
      const translation = translationSnapshot.docs[0]?.data()
      
      if (!translation) {
        console.warn(`⚠️ No English translation found for challenge ${id}`)
        return null
      }
      
      console.log(`✅ Found challenge: ${challengeDoc.id}`)
      
      return {
        id: challengeDoc.id,
        name: translation.name,
        description: translation.description,
        category: baseChallenge.categoryId || 'general',
        icon: baseChallenge.icon || '🎯',
        status: baseChallenge.status || 'active',
        language: translation.language,
        defaultFrequency: baseChallenge.defaultFrequency || {
          mode: 'daily',
          times: ['09:00'],
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        order: baseChallenge.order || 0,
        totalUsers: baseChallenge.metadata?.totalUsers || 0,
        createdAt: baseChallenge.metadata?.createdAt || new Date(),
        updatedAt: baseChallenge.metadata?.updatedAt || new Date(),
      } as Challenge
      
    } catch (error) {
      console.error('Error fetching challenge:', error)
      throw error
    }
  }

  async createChallenge(challenge: Omit<Challenge, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const now = new Date().toISOString()
      const docRef = await addDoc(collection(db, 'challenges'), {
        ...challenge,
        createdAt: now,
        updatedAt: now,
      })
      return docRef.id
    } catch (error) {
      console.error('Error creating challenge:', error)
      throw error
    }
  }

  async updateChallenge(id: string, updates: Partial<Challenge>): Promise<void> {
    try {
      const docRef = doc(db, 'challenges', id)
      
      // First check if the document exists
      const docSnap = await getDoc(docRef)
      if (!docSnap.exists()) {
        throw new Error(`Challenge with ID "${id}" does not exist in the database. The challenge may have been deleted or the ID is incorrect.`)
      }
      
      await updateDoc(docRef, {
        ...updates,
        updatedAt: new Date().toISOString(),
      })
    } catch (error) {
      console.error('Error updating challenge:', error)
      throw error
    }
  }

  async deleteChallenge(id: string): Promise<void> {
    try {
      const batch = writeBatch(db)
      
      // Delete challenge
      const challengeRef = doc(db, 'challenges', id)
      batch.delete(challengeRef)
      
      // Delete all related translations if they exist
      const translationsRef = collection(db, 'challengeTranslations')
      const translationsQuery = query(translationsRef, where('challengeId', '==', id))
      const translationsSnapshot = await getDocs(translationsQuery)
      
      translationsSnapshot.docs.forEach(doc => {
        batch.delete(doc.ref)
      })
      
      await batch.commit()
    } catch (error) {
      console.error('Error deleting challenge:', error)
      throw error
    }
  }

  // Category Management - Simplified structure
  async getCategories(language: string = 'en'): Promise<Category[]> {
    console.log(`🔍 Fetching categories for language: ${language}`)
    
    try {
      // Normalized structure: get active categories first
      const categoriesRef = collection(db, 'categories')
      const categoriesQuery = query(
        categoriesRef,
        where('status', '==', 'active')
      )
      const categoriesSnapshot = await getDocs(categoriesQuery)
      console.log(`📁 Base categories found: ${categoriesSnapshot.docs.length}`)

      // Get translations for the specified language
      const translationsRef = collection(db, 'categoryTranslations')
      const translationsQuery = query(
        translationsRef,
        where('language', '==', language)
      )
      const translationsSnapshot = await getDocs(translationsQuery)
      console.log(`🌐 Translations found: ${translationsSnapshot.docs.length}`)

      // Create a map of translations by categoryId
      const translationsMap = new Map()
      translationsSnapshot.docs.forEach(doc => {
        const translation = doc.data()
        translationsMap.set(translation.categoryId, {
          id: doc.id,
          ...translation
        })
      })

      // Combine categories with their translations
      const categories = categoriesSnapshot.docs
        .map(doc => {
          const categoryData = doc.data()
          const translation = translationsMap.get(doc.id)
          
          if (!translation) {
            console.warn(`⚠️ No translation found for category ${doc.id} in language ${language}`)
            return null
          }

          return {
            id: doc.id,
            ...categoryData,
            name: translation.name,
            description: translation.description,
            language: translation.language
          } as Category
        })
        .filter((category): category is Category => category !== null) // Remove null entries
        .sort((a, b) => (a.order || 0) - (b.order || 0))

      console.log(`📁 Categories with translations: ${categories.length}`)
      console.log('🎯 Category IDs:', categories.map(c => c.id))
      
      return categories
    } catch (error) {
      console.error('❌ Error fetching categories:', error)
      return []
    }
  }

  async getCategory(id: string): Promise<Category | null> {
    try {
      const docRef = doc(db, 'categories', id)
      const docSnap = await getDoc(docRef)
      return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } as Category : null
    } catch (error) {
      console.error('Error fetching category:', error)
      return null
    }
  }

  async getCategoryBySlug(slug: string): Promise<Category | null> {
    try {
      const q = query(collection(db, 'categories'), where('slug', '==', slug))
      const querySnapshot = await getDocs(q)
      
      if (querySnapshot.empty) {
        return null
      }
      
      const doc = querySnapshot.docs[0]
      return { id: doc.id, ...doc.data() } as Category
    } catch (error) {
      console.error('Error fetching category by slug:', error)
      return null
    }
  }

  async createCategory(category: Omit<Category, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const now = Timestamp.now()
      const currentUser = auth.currentUser
      if (!currentUser) throw new Error('User not authenticated')
      
      const docRef = await addDoc(collection(db, 'categories'), {
        ...category,
        metadata: {
          createdAt: now,
          updatedAt: now,
          createdBy: currentUser.uid
        }
      })
      return docRef.id
    } catch (error) {
      console.error('Error creating category:', error)
      throw error
    }
  }

  async updateCategory(id: string, updates: Partial<Category>): Promise<void> {
    try {
      const docRef = doc(db, 'categories', id)
      await updateDoc(docRef, {
        ...updates,
        updatedAt: new Date().toISOString()
      })
    } catch (error) {
      console.error('Error updating category:', error)
      throw error
    }
  }

  async deleteCategory(id: string): Promise<void> {
    try {
      const docRef = doc(db, 'categories', id)
      await deleteDoc(docRef)
    } catch (error) {
      console.error('Error deleting category:', error)
      throw error
    }
  }

  // Translation Management
  async getChallengeTranslations(challengeId: string): Promise<ChallengeTranslation[]> {
    try {
      const translationsRef = collection(db, 'challengeTranslations')
      const q = query(translationsRef, where('challengeId', '==', challengeId))
      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => {
        const data = doc.data()
        return {
          id: doc.id,
          challengeId: data.challengeId,
          language: data.language,
          name: data.name,
          description: data.description,
          createdAt: data.metadata?.createdAt || new Date(),
          updatedAt: data.metadata?.updatedAt || new Date(),
        } as ChallengeTranslation
      })
    } catch (error) {
      console.error('Error fetching challenge translations:', error)
      return []
    }
  }

  async createChallengeTranslation(translation: Omit<ChallengeTranslation, 'id'>): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, 'challengeTranslations'), translation)
      return docRef.id
    } catch (error) {
      console.error('Error creating challenge translation:', error)
      throw error
    }
  }

  async updateChallengeTranslation(id: string, updates: Partial<ChallengeTranslation>): Promise<void> {
    try {
      const docRef = doc(db, 'challengeTranslations', id)
      await updateDoc(docRef, updates)
    } catch (error) {
      console.error('Error updating challenge translation:', error)
      throw error
    }
  }

  async deleteChallengeTranslation(id: string): Promise<void> {
    try {
      const docRef = doc(db, 'challengeTranslations', id)
      await deleteDoc(docRef)
    } catch (error) {
      console.error('Error deleting challenge translation:', error)
      throw error
    }
  }

  async getCategoryTranslations(categoryId: string): Promise<CategoryTranslation[]> {
    try {
      const translationsRef = collection(db, 'categoryTranslations')
      const q = query(translationsRef, where('categoryId', '==', categoryId))
      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CategoryTranslation))
    } catch (error) {
      console.error('Error fetching category translations:', error)
      return []
    }
  }

  async createCategoryTranslation(translation: Omit<CategoryTranslation, 'id'>): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, 'categoryTranslations'), translation)
      return docRef.id
    } catch (error) {
      console.error('Error creating category translation:', error)
      throw error
    }
  }

  async updateCategoryTranslation(id: string, updates: Partial<CategoryTranslation>): Promise<void> {
    try {
      console.log(`🔄 Updating category translation ${id} with:`, updates)
      const docRef = doc(db, 'categoryTranslations', id)
      
      // Add updatedAt timestamp
      const updateData = {
        ...updates,
        updatedAt: new Date()
      }
      
      await updateDoc(docRef, updateData)
      console.log(`✅ Successfully updated category translation ${id}`)
      
      // Verify the update was successful
      const updatedDoc = await getDoc(docRef)
      if (updatedDoc.exists()) {
        const data = updatedDoc.data()
        console.log(`🔍 Verification - Updated document data:`, {
          id,
          name: data.name,
          language: data.language,
          updatedAt: data.updatedAt
        })
      }
    } catch (error) {
      console.error('❌ Error updating category translation:', error)
      throw error
    }
  }

  async deleteCategoryTranslation(id: string): Promise<void> {
    try {
      const docRef = doc(db, 'categoryTranslations', id)
      await deleteDoc(docRef)
    } catch (error) {
      console.error('Error deleting category translation:', error)
      throw error
    }
  }

  // Clear all categories and translations (for re-seeding)
  async clearAllCategories(): Promise<void> {
    console.log('🗑️ Clearing all categories and translations...')
    
    try {
      // Get all categories
      const categoriesRef = collection(db, 'categories')
      const categoriesSnapshot = await getDocs(categoriesRef)
      console.log(`📁 Found ${categoriesSnapshot.docs.length} categories to delete`)

      // Get all category translations
      const translationsRef = collection(db, 'categoryTranslations')
      const translationsSnapshot = await getDocs(translationsRef)
      console.log(`🌐 Found ${translationsSnapshot.docs.length} translations to delete`)

      // Delete all category translations first
      for (const doc of translationsSnapshot.docs) {
        await deleteDoc(doc.ref)
        console.log(`🗑️ Deleted translation: ${doc.id}`)
      }

      // Delete all categories
      for (const doc of categoriesSnapshot.docs) {
        await deleteDoc(doc.ref)
        console.log(`🗑️ Deleted category: ${doc.id}`)
      }

      console.log('✅ Successfully cleared all categories and translations')
    } catch (error) {
      console.error('❌ Error clearing categories:', error)
      throw error
    }
  }

  // User Management - Updated with better debugging and flexible querying
  async getUsers(): Promise<User[]> {
    console.log('🔍 Fetching users from Firestore...')
    
    try {
      const usersRef = collection(db, 'users')
      
      // Try to get all users first to understand structure
      const allUsersSnapshot = await getDocs(usersRef)
      console.log(`📊 Total documents in users collection: ${allUsersSnapshot.size}`)
      
      if (allUsersSnapshot.size > 0) {
        // Log sample documents
        const sampleDocs = allUsersSnapshot.docs.slice(0, 3)
        console.log('🔍 Sample user documents:')
        sampleDocs.forEach(doc => {
          const data = doc.data()
          console.log(`  - ${doc.id}:`, {
            displayName: data.displayName,
            email: data.email,
            role: data.role,
            status: data.status,
            createdAt: data.createdAt
          })
        })
        
        // Get all users and filter appropriately
        const allUsers = allUsersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as User[]
        
        // Filter out deleted users unless we want to see them
        const activeUsers = allUsers.filter(user => 
          !user.status || user.status !== 'deleted'
        )
        
        console.log(`✅ Found ${allUsers.length} total users, ${activeUsers.length} active users`)
        return allUsers // Return all for admin to see everything
      }
      
      console.log('⚠️ No users found in users collection')
      return []
      
    } catch (error) {
      console.error('❌ Error fetching users:', error)
      
      // Try a simple query without any filters as fallback
      try {
        console.log('🔄 Trying simple query as fallback...')
        const usersRef = collection(db, 'users')
        const simpleSnapshot = await getDocs(usersRef)
        
        const simpleUsers = simpleSnapshot.docs.map(doc => {
          const data = doc.data()
          return {
            id: doc.id,
            displayName: data.displayName || 'No name',
            email: data.email || 'No email',
            role: data.role || 'user',
            status: data.status || 'active',
            locale: data.locale || 'en',
            photoURL: data.photoURL,
            settings: data.settings || { notifications: true, analytics: true },
            createdAt: data.createdAt || new Date().toISOString(),
            updatedAt: data.updatedAt || new Date().toISOString(),
            lastLoginAt: data.lastLoginAt,
          }
        }) as User[]
        
        console.log(`✅ Fallback query found ${simpleUsers.length} users`)
        return simpleUsers
      } catch (fallbackError) {
        console.error('❌ Fallback query also failed:', fallbackError)
        return []
      }
    }
  }

  async getUser(id: string): Promise<User | null> {
    try {
      const docRef = doc(db, 'users', id)
      const docSnap = await getDoc(docRef)
      return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } as User : null
    } catch (error) {
      console.error('Error fetching user:', error)
      return null
    }
  }

  async updateUser(id: string, updates: Partial<User>): Promise<void> {
    try {
      const docRef = doc(db, 'users', id)
      await updateDoc(docRef, {
        ...updates,
        updatedAt: new Date().toISOString(),
      })
    } catch (error) {
      console.error('Error updating user:', error)
      throw error
    }
  }

  async deleteUser(id: string): Promise<void> {
    try {
      // Hard delete - permanently remove user from database
      const docRef = doc(db, 'users', id)
      await deleteDoc(docRef)
    } catch (error) {
      console.error('Error deleting user:', error)
      throw error
    }
  }

  // Admin Statistics
  async getAdminStats(): Promise<AdminStats> {
    try {
      const [categoriesSnapshot, usersSnapshot, challengesSnapshot, languagesSnapshot] = await Promise.all([
        getDocs(collection(db, 'categories')),
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'challenges')),
        getDocs(collection(db, 'languages')),
      ])

      return {
        challengesCount: challengesSnapshot.size,
        categoriesCount: categoriesSnapshot.size,
        usersCount: usersSnapshot.size,
        languagesCount: languagesSnapshot.size,
      }
    } catch (error) {
      console.error('Error fetching admin stats:', error)
      return {
        challengesCount: 0,
        categoriesCount: 0,
        usersCount: 0,
        languagesCount: 0,
      }
    }
  }

  async getRecentChallenges(limitCount: number = 5): Promise<any[]> {
    try {
      const challengesRef = collection(db, 'challenges')
      const q = query(challengesRef, orderBy('createdAt', 'desc'), limit(limitCount))
      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
    } catch (error) {
      console.error('Error fetching recent challenges:', error)
      return []
    }
  }

  async getRecentUsers(limitCount: number = 5): Promise<any[]> {
    try {
      const usersRef = collection(db, 'users')
      const q = query(usersRef, orderBy('createdAt', 'desc'), limit(limitCount))
      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
    } catch (error) {
      console.error('Error fetching recent users:', error)
      return []
    }
  }

  // Language Management
  async getLanguages(): Promise<Language[]> {
    console.log('🔍 Fetching languages from database...')
    
    try {
      const languagesRef = collection(db, 'languages')
      // Simplified query to avoid composite index requirement
      const languagesQuery = query(languagesRef, where('isActive', '==', true))
      const languagesSnapshot = await getDocs(languagesQuery)
      
      const languages = languagesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Language[]
      
      // Sort by order and then by createdAt in memory to avoid complex index
      languages.sort((a, b) => {
        if (a.order !== b.order) {
          return a.order - b.order
        }
        const aTime = typeof a.createdAt === 'string' ? new Date(a.createdAt).getTime() : a.createdAt.toMillis()
        const bTime = typeof b.createdAt === 'string' ? new Date(b.createdAt).getTime() : b.createdAt.toMillis()
        return aTime - bTime
      })
      
      console.log(`✅ Found ${languages.length} active languages`)
      return languages
      
    } catch (error) {
      console.error('❌ Error fetching languages:', error)
      
      // Return default languages as fallback
      console.log('🔄 Using fallback default languages')
      return this.getDefaultLanguages()
    }
  }

  async getAllLanguages(): Promise<Language[]> {
    console.log('🔍 Fetching all languages (including inactive)...')
    
    try {
      const languagesRef = collection(db, 'languages')
      const languagesQuery = query(languagesRef, orderBy('order', 'asc'))
      const languagesSnapshot = await getDocs(languagesQuery)
      
      const languages = languagesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Language[]
      
      // Sort by order and then by createdAt in memory to avoid complex index
      languages.sort((a, b) => {
        if (a.order !== b.order) {
          return a.order - b.order
        }
        const aTime = typeof a.createdAt === 'string' ? new Date(a.createdAt).getTime() : a.createdAt.toMillis()
        const bTime = typeof b.createdAt === 'string' ? new Date(b.createdAt).getTime() : b.createdAt.toMillis()
        return aTime - bTime
      })
      
      console.log(`✅ Found ${languages.length} total languages`)
      return languages
      
    } catch (error) {
      console.error('❌ Error fetching all languages:', error)
      return this.getDefaultLanguages()
    }
  }

  async getLanguage(id: string): Promise<Language | null> {
    try {
      const docRef = doc(db, 'languages', id)
      const docSnap = await getDoc(docRef)
      return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } as Language : null
    } catch (error) {
      console.error('Error fetching language:', error)
      return null
    }
  }

  async getLanguageByCode(code: string): Promise<Language | null> {
    try {
      const q = query(collection(db, 'languages'), where('code', '==', code))
      const querySnapshot = await getDocs(q)
      
      if (querySnapshot.empty) {
        return null
      }
      
      const doc = querySnapshot.docs[0]
      return { id: doc.id, ...doc.data() } as Language
    } catch (error) {
      console.error('Error fetching language by code:', error)
      return null
    }
  }

  async createLanguage(language: Omit<Language, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      // Check if language code already exists
      const existingLanguages = await this.getAllLanguages()
      const codeExists = existingLanguages.some(lang => lang.code.toLowerCase() === language.code.toLowerCase())
      
      if (codeExists) {
        throw new Error(`Language code '${language.code}' already exists`)
      }

      const now = new Date().toISOString()
      const docRef = await addDoc(collection(db, 'languages'), {
        ...language,
        code: language.code.toLowerCase(), // Ensure lowercase
        createdAt: now,
        updatedAt: now,
      })
      
      console.log(`✅ Created language: ${language.name} (${language.code})`)
      return docRef.id
    } catch (error) {
      console.error('Error creating language:', error)
      throw error
    }
  }

  async updateLanguage(id: string, updates: Partial<Language>): Promise<void> {
    try {
      // If updating code, check for conflicts
      if (updates.code) {
        const existingLanguages = await this.getAllLanguages()
        const codeExists = existingLanguages.some(lang => 
          lang.id !== id && lang.code.toLowerCase() === updates.code!.toLowerCase()
        )
        
        if (codeExists) {
          throw new Error(`Language code '${updates.code}' already exists`)
        }
        
        updates.code = updates.code.toLowerCase()
      }

      const docRef = doc(db, 'languages', id)
      await updateDoc(docRef, {
        ...updates,
        updatedAt: new Date().toISOString(),
      })
      
      console.log(`✅ Updated language: ${id}`)
    } catch (error) {
      console.error('Error updating language:', error)
      throw error
    }
  }

  async deleteLanguage(id: string): Promise<void> {
    try {
      const batch = writeBatch(db)
      
      // Delete language
      const languageRef = doc(db, 'languages', id)
      batch.delete(languageRef)
      
      // Note: We might want to keep translations even if language is deleted
      // Or implement a soft delete by setting isActive: false
      // For now, we'll just delete the language record
      
      await batch.commit()
      console.log(`✅ Deleted language: ${id}`)
    } catch (error) {
      console.error('Error deleting language:', error)
      throw error
    }
  }

  async toggleLanguageStatus(id: string): Promise<void> {
    try {
      const language = await this.getLanguage(id)
      if (!language) {
        throw new Error('Language not found')
      }
      
      await this.updateLanguage(id, {
        isActive: !language.isActive
      })
      
      console.log(`✅ Toggled language status: ${id} -> ${!language.isActive}`)
    } catch (error) {
      console.error('Error toggling language status:', error)
      throw error
    }
  }

  private getDefaultLanguages(): Language[] {
    // Fallback languages if database is not available
    const defaultLanguages = [
      { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸', order: 1 },
      { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷', order: 2 },
      { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸', order: 3 },
      { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪', order: 4 },
      { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹', order: 5 },
      { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇵🇹', order: 6 },
    ]

    return defaultLanguages.map((lang) => ({
      id: `default-${lang.code}`,
      ...lang,
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }))
  }

  async seedDefaultLanguages(): Promise<void> {
    try {
      console.log('🌱 Seeding default languages...')
      
      const existingLanguages = await this.getAllLanguages()
      if (existingLanguages.length > 0) {
        console.log('ℹ️ Languages already exist, skipping seed')
        return
      }

      const defaultLanguages = this.getDefaultLanguages()
      
      for (const language of defaultLanguages) {
        const { id, ...languageData } = language
        await this.createLanguage(languageData)
      }
      
      console.log('✅ Default languages seeded successfully')
    } catch (error) {
      console.error('❌ Error seeding default languages:', error)
      throw error
    }
  }


}

export const adminService = new AdminService()